/**
 * FGA Group model interfaces
 *
 * These interfaces define the structure of group entities in Auth0 FGA.
 * Groups are represented as tuples with the format: group:{organisationId}|{groupName}
 *
 * @packageDocumentation
 */

/**
 * Represents an FGA group entity.
 * Maps to FGA tuple format: `group:{organisationId}|{groupName}`
 */
export interface FgaGroup {
  /**
   * Full FGA group identifier
   * @example "group:org_demo|engineering"
   */
  id: string;

  /**
   * Display name extracted from the group ID
   * @example "engineering"
   */
  name: string;

  /**
   * Organisation this group belongs to
   * @example "org_demo"
   */
  organisationId: string;

  /**
   * Number of members in the group (populated by API)
   */
  memberCount?: number;

  /**
   * List of users that are members of this group (lazy-loaded)
   */
  users?: string[];

  /**
   * Indicates if async operations are in progress for this group
   */
  loading?: boolean;

  /**
   * Creation timestamp from FGA tuple (if available)
   */
  createdAt?: string;

  /**
   * Last modification timestamp
   */
  updatedAt?: string;
}

/**
 * Response from listing groups for an organisation.
 * Corresponds to FGA read operations filtered by group objects.
 */
export interface FgaGroupsResponse {
  /**
   * Array of group identifiers (full FGA format)
   */
  groups: string[];

  /**
   * Organisation the groups belong to
   */
  organisationId: string;

  /**
   * Pagination token for retrieving additional results
   */
  continuationToken?: string;
}

/**
 * Response containing users that belong to a specific group.
 */
export interface FgaGroupUsersResponse {
  /**
   * Group identifier
   */
  groupId: string;

  /**
   * Array of user identifiers that are members of the group
   */
  users: string[];

  /**
   * Pagination token for retrieving additional results
   */
  continuationToken?: string;
}

/**
 * Parsed components from an FGA group identifier.
 * Used for extracting display values from raw FGA IDs.
 */
export interface ParsedGroupId {
  /**
   * Original full ID string
   */
  fullId: string;

  /**
   * Organisation ID extracted from the group ID
   */
  organisationId: string;

  /**
   * Group name extracted from the group ID
   */
  name: string;
}

/**
 * Parses a group ID string into its component parts.
 *
 * @param groupId - Full FGA group identifier
 * @returns Parsed group ID components
 * @throws Error if groupId format is invalid
 *
 * @example
 * ```typescript
 * const parsed = parseGroupId('group:org_demo|engineering');
 * // Returns: { fullId: 'group:org_demo|engineering', organisationId: 'org_demo', name: 'engineering' }
 * ```
 */
export function parseGroupId(groupId: string): ParsedGroupId {
  const match = groupId.match(/^group:([^|]+)\|(.+)$/);
  if (!match) {
    throw new Error(`Invalid group ID format: ${groupId}`);
  }
  return {
    fullId: groupId,
    organisationId: match[1],
    name: match[2]
  };
}

/**
 * Creates a full FGA group identifier from components.
 *
 * @param organisationId - Organisation ID
 * @param name - Group name
 * @returns Full FGA group identifier
 *
 * @example
 * ```typescript
 * const id = buildGroupId('org_demo', 'engineering');
 * // Returns: 'group:org_demo|engineering'
 * ```
 */
export function buildGroupId(organisationId: string, name: string): string {
  return `group:${organisationId}|${name}`;
}

/**
 * Prefix identifying an Auth0 Enterprise Group provisioned through SCIM.
 */
export const SCIM_GROUP_PREFIX = 'grp_';

/**
 * Auth0 group identifier format, copied from the Management API spec for
 * `GET /groups/{id}`: the prefix followed by 14 to 22 base58 characters.
 *
 * The prefix alone is not enough: groups named `grp_...` before the prefix was
 * reserved at creation are FGA-only groups that Auth0 rejects with a 400.
 */
export const SCIM_GROUP_ID_PATTERN = /^grp_[1-9a-km-zA-HJ-NP-Z]{14,22}$/;

/**
 * Determines whether a group is provisioned through SCIM and therefore has
 * membership owned by the source IdP.
 *
 * Must be evaluated against the FGA identifier, not the display name: enterprise
 * group names are enriched with their Auth0 display name, which drops the prefix.
 *
 * @param groupId - Full FGA group identifier, or a bare group name
 * @returns true when the group name is a well-formed Auth0 group identifier
 *
 * @example
 * ```typescript
 * isScimManagedGroupId('group:org_demo|grp_2bT9xQk4LmN7pRsW'); // true
 * isScimManagedGroupId('group:org_demo|grp_team'); // false — local group
 * isScimManagedGroupId('group:org_demo|engineering'); // false
 * ```
 */
export function isScimManagedGroupId(groupId: string | null | undefined): boolean {
  if (!groupId) {
    return false;
  }

  const name = /^group:[^|]+\|(.+)$/.exec(groupId)?.[1] ?? groupId;
  return SCIM_GROUP_ID_PATTERN.test(name);
}
